import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, QrCode, RefreshCw, Copy, Check, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface VendorDepositQRCodeProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
  orderNumber: string;
}

export const VendorDepositQRCode = ({ open, onClose, onSuccess, orderId, orderNumber }: VendorDepositQRCodeProps) => {
  const [loading, setLoading] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      generateQRCode();
    }
  }, [open, orderId]);

  useEffect(() => {
    // Reset state when dialog closes
    if (!open) {
      setQrSvg(null);
      setOtpCode(null);
      setExpiresAt(null);
      setError(null);
    }
  }, [open]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Generating QR code for order:", orderId);

      const { data, error: invokeError } = await supabase.functions.invoke('generate-vendor-pickup-token', {
        body: { orderId }
      });

      console.log("Response:", data, invokeError);

      if (invokeError) {
        console.error("Invoke error:", invokeError);
        throw new Error(invokeError.message || "Erreur lors de l'appel à la fonction");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.success && data?.token) {
        setQrSvg(data.token.qr_svg);
        setOtpCode(data.token.otp_code);
        setExpiresAt(data.token.expires_at);
        toast.success("QR code généré avec succès");
      } else {
        throw new Error("Réponse invalide du serveur");
      }
    } catch (err: any) {
      console.error("Error generating QR code:", err);
      const errorMessage = err.message || "Erreur lors de la génération du QR code";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyOTP = async () => {
    if (otpCode) {
      await navigator.clipboard.writeText(otpCode);
      setCopied(true);
      toast.success("Code copié !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatExpiryDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
            <QrCode className="w-5 h-5" />
            QR Code de dépôt
          </DialogTitle>
          <DialogDescription className="text-xs md:text-sm">
            Présentez ce QR code au responsable du point relais pour déposer la commande #{orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">Génération du QR code...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <p className="text-destructive font-medium mb-2">Erreur</p>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={generateQRCode} disabled={loading} className="w-full sm:w-auto">
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </div>
          ) : qrSvg ? (
            <>
              {/* QR Code Display - Responsive */}
              <Card className="border-2 border-primary/20 bg-white">
                <CardContent className="p-4 md:p-6">
                  <div className="flex justify-center items-center">
                    <div 
                      className="w-full max-w-[280px] flex justify-center items-center"
                      dangerouslySetInnerHTML={{ __html: qrSvg }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900 text-center">
                  💡 Scannez ce QR code avec l'application du point relais
                </p>
              </div>

              {/* OTP Code Display - Mobile Optimized */}
              {otpCode && (
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="text-xs md:text-sm text-muted-foreground mb-2">
                        Code de confirmation (si scan impossible)
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl md:text-3xl font-mono font-bold tracking-widest text-orange-600">
                          {otpCode}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleCopyOTP}
                          className="shrink-0"
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Communiquez ce code au responsable du point relais
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Expiry Info */}
              {expiresAt && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-center text-muted-foreground">
                    ⏰ Valide jusqu'au {formatExpiryDate(expiresAt)}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={generateQRCode}
                  disabled={loading}
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Régénérer
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1"
                >
                  Fermer
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <QrCode className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4 text-sm">
                Aucun QR code disponible
              </p>
              <Button onClick={generateQRCode} disabled={loading} className="w-full sm:w-auto">
                <QrCode className="h-4 w-4 mr-2" />
                Générer le QR code
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
